import { type Address } from 'viem';
import { fetchEnvironmentConfig } from '../../../config/environment';

// Contract addresses (dynamic based on environment)
// Production: BSC Mainnet (ChainID: 56)
// Development: Sepolia Testnet (ChainID: 11155111) - displayed as "BSC Mainnet" in UI

/**
 * Get contract addresses based on environment (dev/prod)
 * Fetches from backend config API
 */
export async function getStakingAddresses() {
  const config = await fetchEnvironmentConfig();
  return {
    FAR_TOKEN: config.contracts.staking.farToken as Address,
    STAKING_CONTRACT: config.contracts.staking.stakingContract as Address,
    CHAIN_ID: config.contracts.staking.chainId,
    CHAIN_NAME: config.contracts.staking.chainName,
  };
}

// Fallback constants for synchronous access (production values)
export const FAR_TOKEN: Address = '0xC44F9f08E524669E5DEeEBC9EB142c81edfAd178'; // FAR (main BSC token)
export const STAKING_CONTRACT: Address = '0x9f902fac7655985eB0961b3A818aa7D597682Cdc'; // Token Stake contract

// Staking configuration - DEPRECATED: Use contract values instead
// These are fallback values only if contract read fails
export const MIN_LOCK_DAYS_FALLBACK = 5;
export const MAX_LOCK_DAYS_FALLBACK = 180;
export const MIN_MULTIPLIER = 1.0; // 1x
export const MAX_MULTIPLIER = 2.0; // 2x

// Position Info Interface (matches StakingPosition struct from contract)
export interface PositionInfo {
  amount: bigint;
  unlockTime: bigint;
  lockMultiplier: bigint;
  lastRewardTime: bigint;
  rewardDebt: bigint;
  accRewardPerWeightPaid: bigint;
}

// ERC20 ABI (minimal)
export const ERC20_ABI = [
  {
    inputs: [{ name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

// Staking Contract ABI (FARStakingEmission)
export const STAKING_ABI = [
  {
    inputs: [
      { name: 'amount', type: 'uint256' },
      { name: 'unlockTime', type: 'uint256' },
    ],
    name: 'createPosition',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: 'positionId', type: 'uint256' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'withdraw',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ name: 'positionId', type: 'uint256' }],
    name: 'claimRewards',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'claimAllRewards',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ name: 'user', type: 'address' }],
    name: 'getUserPositions',
    outputs: [
      {
        name: '',
        type: 'tuple[]',
        components: [
          { name: 'amount', type: 'uint256' },
          { name: 'unlockTime', type: 'uint256' },
          { name: 'lockMultiplier', type: 'uint256' },
          { name: 'lastRewardTime', type: 'uint256' },
          { name: 'rewardDebt', type: 'uint256' },
          { name: 'accRewardPerWeightPaid', type: 'uint256' },
        ],
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ name: 'user', type: 'address' }],
    name: 'getPendingRewards',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'getTotalStaked',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'getActiveStakersCount',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ name: 'user', type: 'address' }],
    name: 'getUserClaimedRewards',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'emissionPerSecond',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ name: 'user', type: 'address' }],
    name: 'getTotalPendingRewards',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'getEnhancedContractStats',
    outputs: [
      {
        name: '',
        type: 'tuple',
        components: [
          { name: 'contractTotalStaked', type: 'uint256' },
          { name: 'contractTotalWeight', type: 'uint256' },
          { name: 'contractEmissionRate', type: 'uint256' },
          { name: 'contractDailyEmission', type: 'uint256' },
          { name: 'contractTotalClaimed', type: 'uint256' },
          { name: 'contractAvailableRewards', type: 'uint256' },
          { name: 'contractAvgMultiplier', type: 'uint256' },
          { name: 'contractEstimatedDaily', type: 'uint256' },
        ],
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ name: 'user', type: 'address' }],
    name: 'getUserDashboard',
    outputs: [
      {
        name: '',
        type: 'tuple',
        components: [
          { name: 'userTotalStaked', type: 'uint256' },
          { name: 'userTotalWeight', type: 'uint256' },
          { name: 'userTotalPending', type: 'uint256' },
          { name: 'userPositionCount', type: 'uint256' },
          { name: 'userMultiplier', type: 'uint256' },
          { name: 'userShareOfPool', type: 'uint256' },
        ],
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { name: 'user', type: 'address' },
      { name: 'positionId', type: 'uint256' },
    ],
    name: 'getPositionDetails',
    outputs: [
      {
        name: '',
        type: 'tuple',
        components: [
          { name: 'posAmount', type: 'uint256' },
          { name: 'posUnlockTime', type: 'uint256' },
          { name: 'posLockMultiplier', type: 'uint256' },
          { name: 'posPersonalMultiplier', type: 'uint256' },
          { name: 'posTotalMultiplier', type: 'uint256' },
          { name: 'posWeight', type: 'uint256' },
          { name: 'posPendingRewards', type: 'uint256' },
          { name: 'posIsUnlocked', type: 'bool' },
          { name: 'posDaysUntilUnlock', type: 'uint256' },
        ],
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { name: 'user', type: 'address' },
      { name: 'amount', type: 'uint256' },
      { name: 'lockDays', type: 'uint256' },
    ],
    name: 'previewPosition',
    outputs: [
      {
        name: '',
        type: 'tuple',
        components: [
          { name: 'previewLockMultiplier', type: 'uint256' },
          { name: 'previewTotalMultiplier', type: 'uint256' },
          { name: 'previewWeight', type: 'uint256' },
          { name: 'previewDailyRewards', type: 'uint256' },
          { name: 'previewAPY', type: 'uint256' },
        ],
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ name: 'user', type: 'address' }],
    name: 'getUserEstimatedAPY',
    outputs: [{ name: 'userEstimatedAPY', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { name: 'positionId', type: 'uint256' },
      { name: 'newUnlockTime', type: 'uint256' },
    ],
    name: 'extendLock',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: 'user', type: 'address' },
      { name: 'positionId', type: 'uint256' },
    ],
    name: 'getPendingRewards',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'getLockConfig',
    outputs: [
      {
        name: '',
        type: 'tuple',
        components: [
          { name: 'minLockDays', type: 'uint256' },
          { name: 'maxLockDays', type: 'uint256' },
          { name: 'minMultiplier', type: 'uint256' },
          { name: 'maxMultiplier', type: 'uint256' },
        ],
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
] as const;
